import React, { useState, useEffect } from 'react';
import { Search, Calendar, Clock, Mail, User, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from './ui/sonner';
import { getAuthHeaders, checkTokenExpiration } from '../lib/auth';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });

  useEffect(() => {
    fetchBookings();
  }, [page, search]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.axiosdevelopment.dev';
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
      });
      if (search) params.append('search', search);

      const response = await fetch(`${apiUrl}/api/bookings?${params}`, {
        headers: getAuthHeaders(),
      });

      if (checkTokenExpiration(response)) return;

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch bookings');
      }
      if (data.success) {
        setBookings(data.bookings);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to fetch bookings', {
        description: error.message || 'Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCreatedAt = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Bookings</h1>
          <p className="text-[#888680]">Calendar bookings from your booking page</p>
        </div>
      </div>

      <Card className="border-[#3f4816] bg-[#302f2c]">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#888680]" />
            <Input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-10 bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06]"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#3f4816] bg-[#302f2c]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="h-5 w-5 text-[#d9fb06]" />
            Bookings ({pagination.total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-[#888680]">Loading bookings...</div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-8 text-[#888680]">No bookings yet</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#3f4816]">
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Name</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Email</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Phone</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Date & time</th>
                      <th className="text-left py-3 px-4 text-[#888680] font-medium text-sm">Booked at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id} className="border-b border-[#3f4816] hover:bg-[#3f4816]/30">
                        <td className="py-3 px-4 text-white flex items-center gap-2">
                          <User className="h-4 w-4 text-[#888680] shrink-0" />
                          {booking.name}
                        </td>
                        <td className="py-3 px-4">
                          <a href={`mailto:${booking.email}`} className="text-[#d9fb06] hover:underline flex items-center gap-2">
                            <Mail className="h-4 w-4 shrink-0" />
                            {booking.email}
                          </a>
                        </td>
                        <td className="py-3 px-4 text-[#888680] flex items-center gap-2">
                          <Phone className="h-4 w-4 shrink-0" />
                          {booking.phone || '—'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 text-[#888680]">
                            <Calendar className="h-4 w-4 text-[#d9fb06] shrink-0" />
                            <div>
                              <div className="text-white text-sm">{formatDate(booking.consultationDate)}</div>
                              <div className="flex items-center gap-1 mt-0.5 text-sm">
                                <Clock className="h-3 w-3" />
                                {booking.consultationTime || '—'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-[#888680] text-sm">{formatCreatedAt(booking.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.pages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#3f4816]">
                  <div className="text-sm text-[#888680]">
                    Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} bookings
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="border-[#3f4816] text-[#888680] disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= pagination.pages}
                      className="border-[#3f4816] text-[#888680] disabled:opacity-50"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Bookings;
